// dither3d.js — dependency-free software renderer for the aquaOS boot splash.
// An isometric (orthographic) grid of extruded voxel cubes: each cell samples a
// rasterised glyph, an ORDERED DITHER threshold map decides on/off, and "on"
// cells extrude toward the camera so the logo reads as a 1-bit relief.
// setProgress(p) sweeps the reveal; per-cell noise delays make it materialise
// cell by cell. Same spirit as apps/safari/retro3d.js: painter's-algorithm
// ordering, flat shading quantised to fixed palette ramps, depth fog, and a
// deliberately low internal resolution the host upscales with image-rendering:
// pixelated.
//
//   const scene = createDitherScene(canvas, { mask: { path, viewBox } });
//   scene.setProgress(0.42); scene.setThresholdMap('bayer8'); scene.destroy();

import {
  BG,
  clamp,
  smoothstep,
  buildColorTable,
  buildDelays,
  buildMask,
  getThresholdMap,
  THRESHOLD_MAPS,
} from './dither-maps.js';

export { THRESHOLD_MAPS };

const DEG = Math.PI / 180;
const FOG_STEPS = 7;
const FOG_MAX = 0.86;
const COLORS = buildColorTable(FOG_STEPS, FOG_MAX);
const LEVELS = COLORS[0][0].length;

// flat lambert for the three visible faces, light from the upper-left-front
const SHADE_TOP = 1;
const SHADE_LEFT = 0.66; // +y face, turns toward the light
const SHADE_RIGHT = 0.42; // +x face, turns away

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export function createDitherScene(canvas, opts = {}) {
  const ctx = canvas.getContext('2d');
  const COLS = clamp(opts.cols || 72, 8, 96);
  const ROWS = clamp(opts.rows || 72, 8, 96);
  const N = COLS * ROWS;
  const INTERNAL_W = clamp(opts.internalWidth || 520, 200, 720);
  const MAX_H = opts.cubeHeight ?? 2.6;
  const HALF = opts.cubeSize ?? 0.46; // half footprint, < 0.5 leaves a hairline gap
  const REVEAL = opts.revealDuration ?? 0.24;
  const CENTER_Y = opts.centerY ?? 0.5; // vertical anchor of the grid, 0..1
  const AUTO_SECONDS = opts.autoSeconds ?? 4.4;
  const reduced = !!opts.reducedMotion;
  const autoplay = !!opts.autoplay;

  const { luma, cov } = buildMask({ ...(opts.mask || {}), cols: COLS, rows: ROWS });
  const delays = buildDelays(COLS, ROWS, opts.seed ?? 0x51ed);
  const on = new Uint8Array(N);
  const ramp = new Uint8Array(N); // 0 = grey, 1 = gold
  const fogIdx = new Uint8Array(N);
  // gold for the glyph itself plus the hottest cells of its halo (a scattered
  // gold spray around the logo), grey-green for the rest of the field
  for (let i = 0; i < N; i++) ramp[i] = cov[i] >= 0.5 || luma[i] > 0.52 ? 1 : 0;

  let mapId = opts.thresholdMap || 'voidcluster';
  applyThreshold();

  // ------------------------------------------------------------- state ---
  let W = INTERNAL_W;
  let H = Math.round(INTERNAL_W * 0.6);
  let progress = reduced ? 1 : 0;
  let raf = 0;
  let lastT = 0;
  let destroyed = false;
  let clock = 0;

  function applyThreshold() {
    const { size, data } = getThresholdMap(mapId);
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const i = r * COLS + c;
        on[i] = luma[i] > data[(r % size) * size + (c % size)] ? 1 : 0;
      }
    }
  }

  // Depth fog is keyed to the painter depth (r + c): the far corner of the
  // grid dissolves into the background, the near half stays clean.
  {
    const span = COLS + ROWS - 2 || 1;
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const d = (r + c) / span;
        const fog = smoothstep(0.62, 0.02, d);
        fogIdx[r * COLS + c] = Math.round(fog * (FOG_STEPS - 1));
      }
    }
  }

  function fitResolution() {
    const rect = canvas.getBoundingClientRect();
    const aspect = rect.width > 2 && rect.height > 2 ? rect.height / rect.width : 0.6;
    W = INTERNAL_W;
    H = clamp(Math.round(INTERNAL_W * aspect), 160, 520);
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;
  }

  // ------------------------------------------------------------ render ---

  function render() {
    const e = reduced ? 1 : easeOutCubic(clamp(progress, 0, 1));
    // camera settles: a touch of extra yaw + a lower pitch at the start, easing
    // into the canonical 45 / 35 isometric as the system comes into focus.
    const az = 45 * DEG + (1 - e) * 7 * DEG;
    const ax = 35 * DEG - (1 - e) * 7 * DEG;
    const cosAz = Math.cos(az);
    const sinAz = Math.sin(az);
    const cosAx = Math.cos(ax);
    const sinAx = Math.sin(ax);

    // fit to width (the far/near corners of the diamond crop off-screen, which
    // is what we want for a full-bleed backdrop), then a slow zoom-out.
    const spanX = ((COLS + ROWS) / 2) * cosAz;
    const k = ((W * 0.99) / 2 / spanX) * (1.2 - 0.2 * e);

    const exX = cosAz * k;
    const exY = sinAz * cosAx * k;
    const eyX = -sinAz * k;
    const eyY = cosAz * cosAx * k;
    const ezY = -sinAx * k;

    const cx = W / 2;
    const cy = H * CENTER_Y + MAX_H * sinAx * k * 0.34;

    // ---- ground: a full-bleed vertical ramp so the far plane melts into the
    // background and the near plane catches a little light ----
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, BG);
    grad.addColorStop(0.34, '#111716');
    grad.addColorStop(1, '#1e2624');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    const gx = (COLS - 1) / 2;
    const gy = (ROWS - 1) / 2;
    const px = (x, y) => cx + x * exX + y * eyX;
    const py = (x, y) => cy + x * exY + y * eyY;

    // ---- recessed cell grid: long iso lines, overscanned past the grid so the
    // floor reads as endless instead of a floating diamond ----
    const OVER = Math.ceil(COLS * 0.75);
    const x0 = -gx - 0.5 - OVER;
    const x1 = gx + 0.5 + OVER;
    const y0 = -gy - 0.5 - OVER;
    const y1 = gy + 0.5 + OVER;
    ctx.beginPath();
    for (let x = x0; x <= x1; x += 2) {
      ctx.moveTo(px(x, y0), py(x, y0));
      ctx.lineTo(px(x, y1), py(x, y1));
    }
    for (let y = y0; y <= y1; y += 2) {
      ctx.moveTo(px(x0, y), py(x0, y));
      ctx.lineTo(px(x1, y), py(x1, y));
    }
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(0,0,0,0.42)';
    ctx.stroke();

    // ---- cube corner offsets (affine: same for every cell) ----
    const aX = -HALF * exX - HALF * eyX;
    const aY = -HALF * exY - HALF * eyY;
    const bX = HALF * exX - HALF * eyX;
    const bY = HALF * exY - HALF * eyY;
    const cX = HALF * exX + HALF * eyX;
    const cY = HALF * exY + HALF * eyY;
    const dX = -HALF * exX + HALF * eyX;
    const dY = -HALF * exY + HALF * eyY;

    // ---- raised cubes, painter's algorithm by depth = row + col ----
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(0,0,0,0.72)';
    const diagonals = ROWS + COLS - 1;
    for (let d = 0; d < diagonals; d++) {
      const rStart = Math.max(0, d - COLS + 1);
      const rEnd = Math.min(ROWS - 1, d);
      for (let r = rStart; r <= rEnd; r++) {
        const c = d - r;
        const i = r * COLS + c;
        if (!on[i]) continue;
        const cp = reduced ? 1 : smoothstep(delays[i], delays[i] + REVEAL, progress);
        if (cp <= 0.004) continue;

        const x = c - gx;
        const y = r - gy;
        const sx = cx + x * exX + y * eyX;
        const sy = cy + x * exY + y * eyY;
        if (sx < -24 || sx > W + 24 || sy < -40 || sy > H + 40) continue;

        const h = MAX_H * (0.12 + 0.88 * easeOutCubic(cp));
        const top = h * ezY;
        const pal = COLORS[ramp[i]][fogIdx[i]];
        const bright = 0.52 + 0.48 * cp;
        const lvl = Math.round((LEVELS - 1) * clamp(0.34 + 0.66 * bright, 0, 1) * SHADE_TOP);
        const lTop = clamp(lvl, 0, LEVELS - 1);
        const lLeft = clamp(Math.round(lvl * SHADE_LEFT) - 1, 0, LEVELS - 1);
        const lRight = clamp(Math.round(lvl * SHADE_RIGHT) - 1, 0, LEVELS - 1);

        // +y face (screen-left) — brighter, faces the light
        ctx.beginPath();
        ctx.moveTo(sx + dX, sy + dY + top);
        ctx.lineTo(sx + cX, sy + cY + top);
        ctx.lineTo(sx + cX, sy + cY);
        ctx.lineTo(sx + dX, sy + dY);
        ctx.closePath();
        ctx.fillStyle = pal[lLeft];
        ctx.fill();

        // +x face (screen-right) — in shadow
        ctx.beginPath();
        ctx.moveTo(sx + bX, sy + bY + top);
        ctx.lineTo(sx + cX, sy + cY + top);
        ctx.lineTo(sx + cX, sy + cY);
        ctx.lineTo(sx + bX, sy + bY);
        ctx.closePath();
        ctx.fillStyle = pal[lRight];
        ctx.fill();

        // top face — lightest, with a 1px black edge for the chunky retro read
        ctx.beginPath();
        ctx.moveTo(sx + aX, sy + aY + top);
        ctx.lineTo(sx + bX, sy + bY + top);
        ctx.lineTo(sx + cX, sy + cY + top);
        ctx.lineTo(sx + dX, sy + dY + top);
        ctx.closePath();
        ctx.fillStyle = pal[lTop];
        ctx.fill();
        ctx.stroke();
      }
    }
  }

  // ------------------------------------------------------------- loop ---
  // Progress is normally pushed in by the host (the boot bar owns the clock),
  // so a frame is only drawn when it actually changes. `autoplay` runs an
  // internal timeline instead, for standalone use.

  function step(t) {
    raf = 0;
    if (destroyed) return;
    const dt = lastT ? clamp((t - lastT) / 1000, 0, 0.05) : 0.016;
    lastT = t;
    clock += dt;
    progress = clamp(clock / AUTO_SECONDS, 0, 1);
    render();
    if (progress >= 1) {
      lastT = 0;
      return; // settled — stop burning frames while the login screen fades in
    }
    schedule();
  }

  // The browser already parks rAF for hidden documents; visibilitychange below
  // is the explicit pause/resume so we never leave a frame queued on teardown.
  function schedule() {
    if (destroyed || reduced || raf) return;
    raf = requestAnimationFrame(autoplay ? step : paint);
  }

  function paint() {
    raf = 0;
    if (!destroyed) render();
  }

  function onVisibility() {
    if (document.hidden) {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      lastT = 0;
    } else {
      schedule();
    }
  }

  const ro =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          fitResolution();
          render();
        })
      : null;
  ro?.observe(canvas);
  document.addEventListener('visibilitychange', onVisibility);

  // ---------------------------------------------------------- control ---

  function setProgress(p) {
    if (reduced || autoplay) return;
    const v = clamp(Number(p) || 0, 0, 1);
    if (v === progress) return;
    progress = v;
    schedule();
  }

  function setThresholdMap(id) {
    if (!id || id === mapId) return;
    mapId = id;
    applyThreshold();
    if (reduced) render();
    else schedule();
  }

  function destroy() {
    destroyed = true;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    ro?.disconnect();
    document.removeEventListener('visibilitychange', onVisibility);
  }

  fitResolution();
  render();
  if (!reduced) schedule();

  return { destroy, setProgress, setThresholdMap, THRESHOLD_MAPS };
}
